// WHAT COLOUR IS THIS PART, read off a photograph of it.
//
// For the meshes that come from a supplier's CAD file. The geometry is exact and the
// material is a placeholder — OpenCASCADE writes a metallic default, so a wood dowel
// arrives as a shiny aluminium rod. One colour is most of the way to fixing that.
//
// IN THE BROWSER, because the picture is already on screen. The alternative is posting an
// image id and decoding a JPEG server-side to reach a conclusion the page had already
// drawn — and this way the swatch you approve is computed from the very pixels you are
// looking at. The server takes the three numbers and does not care where they came from.
//
// TWO SOURCES NOW, and the better one is the listing photograph: it is the real object, in
// real light, in its real finish, where a generated photo is a guess at all three. It only
// works for a part you can SEE on the assembled product — a dowel is inside the joint in
// every picture of a chair — which is exactly the set the listing-crops row finds. So a
// crop is sampled through `box`, and the part's own generated photograph is what is left
// for everything else.

export type Rgb = [number, number, number];

// The region to read, normalized 0..1 from the top left — a listing crop's own rectangle.
// Omitted, the whole picture is read, which is what a generated photo of one part on white
// wants.
export type Box = [number, number, number, number];

// How big the picture is sampled at. A generated photo is 1024px and this reads every
// pixel of it; a sixth of that is 30,000 samples, which is far more than a median needs
// and about a millisecond of work.
const SAMPLE_PX = 160;

// WHAT IS BACKGROUND. These photos are a part on white, so anything this bright and this
// unsaturated is the paper it was shot on and not the thing. Both conditions, because a
// pale wood IS bright — what separates it from the background is that it has a hue.
const WHITE_MIN = 232;
const CHROMA_MIN = 10;

// And what is a shadow. Below this the pixel is the dark side of the part or the contact
// shadow under it, and its hue is unreliable in a way that drags the answer grey.
const BLACK_MAX = 18;

// THE BAND THAT ANSWERS. A part in a photograph runs from a specular highlight to a
// terminator, and both ends are lighting rather than colour: the average of all of it is
// always duller and lighter than the material. Between the 35th and 75th percentile of
// luminance is the lit face without the shine, which is what somebody means by "the colour
// of the part".
//
// It is applied WITHIN ONE MATERIAL now (see below), which is the fix for a reading that came
// back as the wall. Applied across everything in the box it answered with the middle of the
// brightness range, and in a rectangle drawn round a chair's rear legs that middle is the
// backdrop and the seat cushion — 60% of the pixels, and pale.
const LO = 0.35;
const HI = 0.75;

// HOW COARSELY MATERIALS ARE TOLD APART, in chromaticity.
//
// Grouping on rgb does not work: a wood frame's lit face and its shadowed face are far apart
// in rgb and are the same material, so a material splits into several groups and each one is
// smaller than the backdrop's single group. Divide a pixel by its own brightness and what is
// left is its colour without its lighting — which is nearly constant across one material and
// genuinely different between wood and a grey wall.
//
// 14 buckets across the useful range of each channel: fine enough to separate warm brown from
// neutral grey, coarse enough that one material stays one bucket.
const CHROMA_BUCKETS = 14;
// And how far from the winning bucket a pixel may sit and still be that material. In the same
// normalized units — a tenth is about the spread of one wood under one light.
const CHROMA_NEAR = 0.1;

// How far apart the four corners may be and still be one backdrop. In `near` units.
const BACKDROP_NEAR = 40;

// AND HOW CLOSE A PIXEL'S COLOUR MUST BE TO THE BACKDROP'S TO BE IT — in chromaticity, not in
// rgb, which is the whole point.
//
// A studio backdrop is not one value: it is a white sweep with a soft gradient, so the haze
// around the object runs from 248 down to 200 while remaining the same surface. Measured in rgb
// that haze is 88 away from the corners and survives; measured without its brightness it is
// 0.005 away and is obviously the wall. That haze was the answer for any box where the part is
// a minority of its own rectangle.
const BACKDROP_CHROMA = 0.045;

// UNLESS IT TAKES ALMOST EVERYTHING, which means the part is the same colour family as the
// sweep — a grey felt pad on a white one. Below this share surviving, chromaticity is not
// telling the two apart and the test falls back to plain distance (see BACKDROP_NEAR), which
// does: 222 and 138 are both neutral and are not the same grey.
const BACKDROP_KEEPS = 0.25;

// Rec. 709, which is what "how bright does this look" means for sRGB.
const luma = (r: number, g: number, b: number) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

const toLinear = (c: number) => {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};

const toSrgb = (v: number) => {
  const c = v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055;
  return Math.max(0, Math.min(255, Math.round(c * 255)));
};

// THE PICTURE'S OWN BACKDROP, from its four corners — or null when it has none.
//
// A retailer's photograph is shot against something: white, or the pale grey this chair is on.
// That surface is the majority of most crops (a box round a chair's rear legs is mostly the
// room behind them) and it is what the sampler used to answer with. It cannot be recognised by
// a threshold — a grey wall is not "bright and unsaturated enough to be paper" — but it can be
// LOOKED UP, because a photograph tells you what it is: whatever is in all four corners.
//
// ALL FOUR HAVE TO AGREE, which is the guard against a lifestyle shot. A chair photographed in
// a kitchen has a plant in one corner and a floor in another; asking that they match within a
// tolerance is what separates "this is the backdrop" from "this is a room", and a room gets no
// backdrop rejection at all.
function backdropOf(img: HTMLImageElement): Rgb | null {
  const n = 24;
  const canvas = document.createElement('canvas');
  canvas.width = n;
  canvas.height = n;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, n, n);
  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, n, n).data;
  } catch {
    return null;
  }
  const at = (x: number, y: number): Rgb => {
    const i = (y * n + x) * 4;
    return [data[i], data[i + 1], data[i + 2]];
  };
  const corners = [at(0, 0), at(n - 1, 0), at(0, n - 1), at(n - 1, n - 1)];
  const mean: Rgb = [0, 1, 2].map(
    (c) => Math.round(corners.reduce((sum, k) => sum + k[c], 0) / corners.length),
  ) as Rgb;
  return corners.every((k) => near(k, mean) < BACKDROP_NEAR) ? mean : null;
}

// WHICH PIXELS OF THE BOX ARE THE PART — polygons tracing it, in the picture's own 0..1
// coordinates, one per piece of it that is visible.
//
// Spelled out here rather than imported, for the same reason `Box` is: this file answers a
// question about pixels and nothing else, so it declares the shapes it is handed. The drawing
// side's copy is realImages' `Outline`, and they are the same four words on purpose.
export type Outline = [number, number][];

// The part's colour in that picture, read THROUGH ITS OUTLINES AND NOWHERE ELSE — or null.
//
// Null is a real answer and the caller must show it as one: a crop nobody traced, a tracing too
// small to average, a picture that failed to load. All three mean the same thing here, which is
// that this photograph has no colour to offer for this part, and the menu simply leaves it out.
//
// NO FALLBACK TO THE RECTANGLE, which is the whole of what this function used to do. A box round
// a chair's rear leg frame is three quarters room; a side rail's box is mostly the seat cushion
// above it; and the box is padded outward besides, so its margin is pure background by
// construction. Every statistic that tried to find the part inside that rectangle was guessing,
// and a guess dressed as a swatch is worse than a gap — you cannot tell rgb(203,193,184) from a
// pale wood by looking at it in a menu.
//
// What is left below still runs, on the masked pixels alone: white, black and the studio sweep
// are dropped, and the chromaticity clustering picks the material that owns the most of them.
// Those clean up the outline's own edge, where one pixel of wall is half wall.
export async function sampleColour(
  src: string, box: Box, outlines: Outline[],
): Promise<Rgb | null> {
  if (!outlines.length) return null;
  const img = new Image();
  // Same-origin — these are /api/notice/catalog/image/<id> — so the canvas stays
  // untainted and getImageData is allowed. Set anyway: a tainted canvas throws on read,
  // which is a worse failure than a picture that does not load.
  img.crossOrigin = 'anonymous';
  img.src = src;
  try {
    await img.decode();
  } catch {
    return null;
  }
  if (!img.naturalWidth || !img.naturalHeight) return null;

  // The part of the picture being read, in its own pixels: the box, which the outlines are
  // then applied within.
  const [bx0, by0, bx1, by1] = box;
  const sx = Math.max(0, Math.round(bx0 * img.naturalWidth));
  const sy = Math.max(0, Math.round(by0 * img.naturalHeight));
  const sw = Math.max(1, Math.round((bx1 - bx0) * img.naturalWidth));
  const sh = Math.max(1, Math.round((by1 - by0) * img.naturalHeight));

  // Scaled from the REGION, not from the picture: a wood button is a dozen pixels of a
  // 1600px photograph, and scaling the photograph to 160 would leave one pixel to average.
  const scale = Math.min(1, SAMPLE_PX / Math.max(sw, sh));
  const w = Math.max(1, Math.round(sw * scale));
  const h = Math.max(1, Math.round(sh * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h);

  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, w, h).data;
  } catch {
    return null;
  }

  // The outlines, rasterized onto the same grid the pixels were read at, so one lookup answers
  // for one pixel. Undrawable — degenerate, or covering almost nothing — is a null answer and
  // not an excuse to read the rectangle instead.
  const inside = insideOutline(outlines, [bx0, by0, bx1, by1], w, h);
  if (!inside) return null;
  return colourFromPixels(data, backdropOf(img), inside);
}

// The outlines as a w×h membership map over the SAME region the pixels came from, or undefined
// if they cannot be drawn.
//
// Filled on a canvas rather than tested point by point: a polygon fill is one call into code
// written for it, where a point-in-polygon loop over 25,000 pixels and 40 edges is a million
// crossings written by hand. The path is in the picture's coordinates and the region is a window
// onto it, so the transform is the window's — offset by its corner, scaled by its size.
//
// EVERY RING AS ITS OWN SUBPATH, closed before the next one begins. A part is often several
// shapes — a frame's backrest and its two legs — and what must NOT happen is the pen travelling
// from the end of one to the start of the next: that stroke closes a region containing
// everything between them, which is the room behind the chair.
function insideOutline(
  outlines: Outline[], region: Box, w: number, h: number,
): ((at: number) => boolean) | undefined {
  const [x0, y0, x1, y1] = region;
  const [rw, rh] = [Math.max(1e-6, x1 - x0), Math.max(1e-6, y1 - y0)];
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return undefined;
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  for (const ring of outlines) {
    if (ring.length < 3) continue;
    ring.forEach(([px, py], i) => {
      const x = ((px - x0) / rw) * w;
      const y = ((py - y0) / rh) * h;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
  }
  ctx.fill();
  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, w, h).data;
  } catch {
    return undefined;
  }
  // A FILL THAT COVERS ALMOST NOTHING is an outline that has gone wrong — a degenerate polygon,
  // or one traced against a different picture. No colour rather than the average of four
  // pixels, and no colour rather than the rectangle it sits in.
  let on = 0;
  for (let i = 0; i < w * h; i++) if (data[i * 4] > 127) on += 1;
  if (on < Math.max(16, w * h * 0.01)) return undefined;
  return (at) => data[at * 4] > 127;
}

// THE ARITHMETIC, on pixels somebody else obtained.
//
// Split out from the canvas work so it can be RUN somewhere other than a browser: the whole
// of what is hard here is which pixels answer the question, and testing that against a real
// photograph is the only way to know that a wood frame comes back as wood rather than as the
// wall behind it. `data` is RGBA rows, as getImageData gives them.
export function colourFromPixels(
  data: Uint8ClampedArray | Uint8Array,
  backdrop?: Rgb | null,
  // ONLY THE PIXELS IT ACCEPTS ARE READ — the outlines, rasterized onto this grid. Everything
  // after it still runs: an outline's edge pixels are half wall, and the chromaticity
  // clustering is what drops them.
  keepAt?: (at: number) => boolean,
): Rgb | null {
  type Px = { l: number; r: number; g: number; b: number; nr: number; ng: number };
  const kept: Px[] = [];
  for (let i = 0; i < data.length; i += 4) {
    const [r, g, b, a] = [data[i], data[i + 1], data[i + 2], data[i + 3]];
    if (a < 200) continue;
    const chroma = Math.max(r, g, b) - Math.min(r, g, b);
    if (r > WHITE_MIN && g > WHITE_MIN && b > WHITE_MIN && chroma < CHROMA_MIN) continue;
    const l = luma(r, g, b);
    if (l < BLACK_MAX) continue;
    if (keepAt && !keepAt(i / 4)) continue;
    // Colour without lighting: the pixel divided by its own brightness. Nearly constant across
    // one material, and different between a warm wood and a neutral wall.
    kept.push({ l, r, g, b, nr: r / l, ng: g / l });
  }

  // THE PICTURE'S OWN BACKDROP, dropped by name and by chromaticity. A studio sweep is not
  // "near white and unsaturated" once it falls into shadow, so the test above lets the haze
  // around the object through — and in a box drawn round a chair leg that haze is the majority.
  //
  // Unless it takes almost everything, in which case the part is the same colour as the wall
  // and rejecting one rejects the other. See BACKDROP_KEEPS.
  // Sixteen readings of a wood button is still sixteen readings of a wood button, and what is
  // being averaged here is a traced part rather than a rectangle it might be a tenth of.
  const floor = 16;
  let pixels = kept;
  if (backdrop) {
    const bl = Math.max(1, luma(backdrop[0], backdrop[1], backdrop[2]));
    const [bnr, bng] = [backdrop[0] / bl, backdrop[1] / bl];
    const byColour = kept.filter(
      (p) => Math.hypot(p.nr - bnr, p.ng - bng) > BACKDROP_CHROMA,
    );
    if (byColour.length >= kept.length * BACKDROP_KEEPS) {
      pixels = byColour;
    } else {
      // The part is as neutral as the sweep. Distance then, which separates them by brightness
      // — and no share test on this tier: a part that is a tenth of its own box is normal, and
      // the only floor that matters is having pixels enough to average.
      const byDistance = kept.filter((p) => near([p.r, p.g, p.b], backdrop) >= BACKDROP_NEAR);
      if (byDistance.length >= floor) pixels = byDistance;
    }
  }
  if (pixels.length < floor) return null;

  // WHICH MATERIAL IS THE ANSWER: the one that owns the most of the TRACED PART. Bucketed by
  // chromaticity, so one material is one bucket whatever the light is doing to it.
  //
  // EVERY PIXEL COUNTS THE SAME. They used to be weighted by how near the middle of the box
  // they were — 1.0 at the centre falling to 0.4 at the edges — because the shape inside the
  // box was unknown and the middle was the best guess at where the part was. The outline
  // answers that now, and on the parts it was written for the guess is inverted: a chair's
  // rear leg frame is a diagonal, so its own pixels are precisely the ones AT the box's edges,
  // and the weighting was quietly discounting the part in favour of the room it crosses.
  const buckets = new Map<string, { nr: number; ng: number; n: number }>();
  for (const p of pixels) {
    const key = `${Math.round(p.nr * CHROMA_BUCKETS)},${Math.round(p.ng * CHROMA_BUCKETS)}`;
    const cell = buckets.get(key) || { nr: 0, ng: 0, n: 0 };
    cell.nr += p.nr;
    cell.ng += p.ng;
    cell.n += 1;
    buckets.set(key, cell);
  }
  const best = [...buckets.values()].reduce((a, b) => (b.n > a.n ? b : a));
  const [cr, cg] = [best.nr / best.n, best.ng / best.n];
  // Everything of that material, bucket edges included: a cell boundary through the middle of
  // one wood would otherwise split it and answer from half of it.
  const material = pixels.filter(
    (p) => Math.hypot(p.nr - cr, p.ng - cg) <= CHROMA_NEAR,
  );

  // And within that material, the lit face without the shine — the band that always answered,
  // now asked of one material instead of of everything in the rectangle.
  material.sort((p, q) => p.l - q.l);
  const from = Math.floor(material.length * LO);
  const to = Math.max(from + 1, Math.floor(material.length * HI));
  const band = material.slice(from, to);

  // Averaged in LINEAR light and converted back, not averaged in sRGB. sRGB is a curve, so
  // a mean taken along it is not the mean of the light — it lands darker, and on a band
  // this wide the difference is visible.
  let r = 0;
  let g = 0;
  let b = 0;
  for (const p of band) {
    r += toLinear(p.r);
    g += toLinear(p.g);
    b += toLinear(p.b);
  }
  return [toSrgb(r / band.length), toSrgb(g / band.length), toSrgb(b / band.length)];
}

export const cssRgb = ([r, g, b]: Rgb) => `rgb(${r} ${g} ${b})`;

// READINGS THAT AGREE ARE ONE ANSWER.
//
// A part found in six photographs, twice in some of them, and holding four generated
// photos of its own is a dozen readings of one piece of wood — and nobody chooses between
// rgb(93, 62, 44) and rgb(94, 63, 45) on merit. The menu was offering positions where there
// were only two or three decisions.
//
// So near-identical readings collapse into one entry, and HOW MANY AGREED is kept, because
// agreement across views is itself evidence: a colour six pictures land on is the material,
// and a colour one picture gives on its own is that picture's light.
//
// LEADER CLUSTERING, on purpose. The first reading of a group defines it and the rest join
// what they are near — no iteration, no centroids moving, the same answer every time from
// the same list. The alternative is k-means for a dozen points, which would decide how many
// colours there are by being asked in advance.
//
// The representative is a MEMBER, not the mean: it is a colour some picture actually gave,
// which keeps the swatch and its provenance the same claim. The one nearest the group's
// average, so it is the least eccentric of them.
export type ColourReading<T> = { rgb: Rgb; source: T };
// `members` is every reading that joined this group, the representative among them. Kept
// because a count says how many agreed and cannot say WHICH — and a map with a dot per
// reading has to draw the ones that agree differently from the ones that stand alone.
export type ColourGroup<T> = {
  rgb: Rgb; source: T; count: number; members: ColourReading<T>[];
};

// How far apart two readings can be and still be the same colour. Weighted toward green
// and away from blue, which is roughly how much the eye cares — the cheap approximation of
// a perceptual distance, and a rigorous one would be a colour-space conversion to decide
// that two browns are the same brown.
const near = (a: Rgb, b: Rgb) => {
  const [dr, dg, db] = [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
  return Math.sqrt(2 * dr * dr + 4 * dg * dg + 3 * db * db);
};
const TOLERANCE = 26;

export function clusterColours<T>(
  readings: ColourReading<T>[], tolerance = TOLERANCE,
): ColourGroup<T>[] {
  const groups: ColourReading<T>[][] = [];
  for (const one of readings) {
    const found = groups.find((g) => near(g[0].rgb, one.rgb) <= tolerance);
    if (found) found.push(one);
    else groups.push([one]);
  }
  return groups
    .map((g) => {
      const mean: Rgb = [0, 1, 2].map((i) =>
        g.reduce((sum, r) => sum + r.rgb[i], 0) / g.length) as Rgb;
      const pick = g.reduce((best, r) => (near(r.rgb, mean) < near(best.rgb, mean) ? r : best));
      return { rgb: pick.rgb, source: pick.source, count: g.length, members: g };
    })
    // The most agreed-upon first: it is the likeliest answer, and the menu is read from
    // the top. A tie keeps the order the readings arrived in, which puts the listing
    // crops — the real object — above the generated photographs.
    .sort((a, b) => b.count - a.count);
}
